import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { observer } from 'mobx-react';
import { createMuiTheme, ThemeProvider, withStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import createPalette from '@material-ui/core/styles/createPalette';
import createTypography from '@material-ui/core/styles/createTypography';
import { fade } from '@material-ui/core/styles/colorManipulator';

import Navigation from './components/navigation/Navigation';
import NavigationSubMenu from './components/navigation/NavigationSubMenu';
import ScenarioListView from './views/scenarios/ScenarioListView';
import CycleWorkflowView from './views/CycleWorkflowView';
import CycleKeyResultsView from './views/global_cockpits/CycleKeyResultsView';
import DemandMapView from './views/global_cockpits/DemandMapView';
import ScenarioCreateView from './views/scenarios/ScenarioCreateView';
import ScenarioDetail from './views/scenarios/ScenarioDetailView';
import ScenarioEditView from './views/scenarios/ScenarioEditView';
import MessagesView from './components/MessagesView';
import DemandView from './views/demand/DemandView';
import TechnologyView from './views/supply/TechnologyView';
import VersionListView from './views/input_files/VersionListView';
import VersionCreateView from './views/input_files/VersionCreateView';
import VersionEditView from './views/input_files/VersionEditView';
import RawAndPacksView from './views/raw_and_packs/RawAndPacks';
import RawAndPacksCommitmentView from './views/raw_and_packs/RawAndPacksCommitment';
import RawAndPacksBomView from './views/raw_and_packs/RawAndPacksBom';
import FgSfgSupplyView from './views/supply/FgSfgSupply';
import ScenarioComparisonView from './views/scenarios/ScenarioComparison';
import DemandDetailView from './views/demand/DemandDetailView';
import DemandBridgesView from './views/demand/DemandBridgesView';
import SupplyDetailView from './views/supply/SupplyDetail';
import PerformanceMonitoringDemandView from './views/performance_monitoring/PerformanceMonitoringDemandView';
import PerformanceMonitoringSupplyView from './views/performance_monitoring/PerformanceMonitoringSupplyView';
import LoginView from './views/LoginView';
import currentUser from './store/currentUser';
import ScrollToTop from './components/utils/ScrollToTop';
import {
  athenaBlue, athenaGrey, athenaGreyBG, athenaRed, gradientBlue,
} from './globals';
import PermissionRoute from './components/permissions/PermissionRoute';
import ScenarioCopyView from './views/scenarios/ScenarioCopyView';
import SupplyMapView from './views/global_cockpits/SupplyMapView';
import HelpView from './views/HelpView';
import InventoryView from "./views/parthenos/InventoryView";
import SupplyView from "./views/parthenos/SupplyView";
import WeeklyDemandView from "./views/parthenos/WeeklyDemandView";
import WeeklyCockpitView from "./views/parthenos/WeeklyCockpitView";
import ScenarioRunReport from "./views/scenarios/ScenarioRunReport";

const styles = theme => ({
  mainContainer: {
    width: '100vw',
    overflow: 'hidden',
  },
  navigation: {
    maxWidth: 162,
    flex: '0 0 162px',
  },
  contentContainer: {
    height: '100%',
    minHeight: '100vh',
  },
  content: {
    padding: theme.spacing(4),
    overflowY: 'scroll',
    overflowX: 'auto',
  },
});


const hSettings = {
  color: athenaGrey,
  fontFamily: 'Josefin Sans, sans-serif',
  fontWeight: 'bold',
};

const theme = createMuiTheme({
  typography: createTypography(createPalette({}), {
    fontFamily: 'Source Sans Pro, Josefin Sans, sans-serif',
    fontSize: 16,
  }),
  palette: {
    primary: {
      light: '#61daff',
      main: athenaBlue,
      dark: '#0079a5',
      contrastText: '#fff',
    },
    secondary: {
      light: '#ff8f93',
      main: athenaRed,
      dark: '#c3243b',
      contrastText: '#fff',
    },
    background: {
      default: athenaGreyBG,
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': "'Source Sans Pro','Josefin Sans'"
      },
    },
    MuiGrid: {
      'grid-xs-true': {
        minWidth: 0,  // Hack to prevent flexboxes from overflowing.
      },
    },
    MuiDrawer: {
      docked: {
        flex: null,
        height: '100%',
      },
      paper: {
        position: null,
        backgroundImage: gradientBlue,
        '& *': {
          color: 'rgba(255, 255, 255, 0.8)'
        },
      },
      paperAnchorDockedLeft: {
        borderRight: null,
      },
    },
    MuiList: {
      root: {
        background: 'transparent',
      },
    },
    MuiButton: {
      root: {
        borderRadius: 8.3,
        fontSize: 15,
        fontWeight: 'bold',
        padding: '6px 25px',
        height: 50,
        fontFamily: 'Source Sans Pro',
      },
      contained: {
        color: athenaBlue,
        backgroundColor: '#FFF',
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.03)',
        '&:hover': {
          backgroundColor: 'rgba(0,0,0, 0.08)',
          boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.03)',
        },
      },
      outlined: {
        backgroundColor: '#FFF',
        color: athenaBlue,
        border: `2px solid ${athenaBlue}`,
        '&:disabled': {
          border: 'none',
        },
      },
      sizeSmall: {
        height: 'unset',
      },
    },
    MuiIconButton: {
      root: {
        '&$disabled svg *': {
          stroke: 'rgba(0, 0, 0, 0.26)',
        },
      },
    },
    MuiTypography: {
      // with CSS element,element Selector will be great
      h1: hSettings,
      h2: hSettings,
      h3: hSettings,
      h4: hSettings,
      h5: hSettings,
      h6: hSettings,
    },
    MuiBreadcrumbs: {
      li: {
        fontFamily: 'Josefin Sans',
      },
    },
    /********/
    /*Tables*/
    /********/
    MuiTableCell: {
      head: {
        color: '#cdcdcd',
      },
      root: {
        borderBottom: 'none',
      },
      stickyHeader: {
        backgroundColor: '#FFF',
        'td&': {
          zIndex: 1,
        },
      },
    },
    MuiTableHead: {
      root: {
        borderBottom: '2px solid rgba(0,0,0,0.05)',
      },
    },
    MuiTableRow: {
      root: {
        '&:nth-child(even)': {
          '&, .MuiTableCell-stickyHeader': {
            backgroundColor: '#f5f5f5',
          },
        },
        '&hover': {
          backgroundColor: fade(athenaBlue, 0.09),
        },
        '&.MuiTableRow-hover:hover': {
          backgroundColor: fade(athenaBlue, 0.09),
        },
      },
    },
    MuiPaper: {
      elevation1: {
        boxShadow: 'none',
      },
      rounded: {
        borderRadius: 10,
      },
    },
    /*******/
    /*Forms*/
    /*******/
    MuiFormControl: {
      root: {
        minWidth: 100,
      },
    },
    MuiInputBase: {
      root: {
        backgroundColor: 'white',
      },
    },
    /*********/
    /*Stepper*/
    /*********/
    MuiStepConnector: {
      alternativeLabel: {
        top: 35,
        left: 'calc(-20% + 20px)',
        right: 'calc(80% + 20px)',
      },
      lineHorizontal :{
        borderTopWidth: 2,
        borderTopColor: '#b1b1b1',
      },
    },
  },
});

@withStyles(styles)
@observer
class App extends React.Component {
  get content() {
    if (currentUser.id === undefined) {
      return <Skeleton variant="rect" width="100%" height={650} />;
    }
    if (!currentUser.is_active) {
      return <LoginView />;
    }
    return (
      <Switch>
        <PermissionRoute path="/" exact component={CycleWorkflowView} />
        <PermissionRoute path="/parthenos/" exact component={WeeklyCockpitView} />
        <PermissionRoute path="/parthenos/supply" exact component={SupplyView} />
        <PermissionRoute path="/parthenos/demand" exact component={WeeklyDemandView} />
        <PermissionRoute path="/parthenos/inventory" exact component={InventoryView} />
        <PermissionRoute path="/cockpits/" exact component={CycleKeyResultsView} />
        <PermissionRoute path="/cockpits/demand-map" exact component={DemandMapView} />
        <PermissionRoute path="/cockpits/supply-map" exact component={SupplyMapView} />
        <Route path="/input-files/" exact component={VersionListView} />
        <PermissionRoute path="/input-files/create" exact component={VersionCreateView} />
        <PermissionRoute path="/input-files/:id/edit" exact render={props =>
          <VersionEditView instanceId={parseInt(props.match.params.id)} />
        } />
        <Route path="/scenarios/" exact component={ScenarioListView} />
        <PermissionRoute path="/scenarios/comparison" exact component={ScenarioComparisonView} />
        <PermissionRoute path="/scenarios/create" exact component={ScenarioCreateView} />
        <PermissionRoute path="/scenarios/:id/detail" exact render={props =>
          <ScenarioDetail instanceId={parseInt(props.match.params.id)} />
        } />
        <PermissionRoute path="/scenarios/:id/run-report" exact render={props =>
          <ScenarioRunReport instanceId={parseInt(props.match.params.id)} />
        } />
        <PermissionRoute path="/scenarios/:id/edit" exact render={props =>
          <ScenarioEditView instanceId={parseInt(props.match.params.id)} />
        } />
        <PermissionRoute path="/scenarios/:id/copy" exact render={props =>
          <ScenarioCopyView instanceId={parseInt(props.match.params.id)} />
        } />
        <PermissionRoute path="/demand/" exact component={DemandView} />
        <PermissionRoute path="/demand/detail" exact component={DemandDetailView} />
        <PermissionRoute path="/demand/bridges" exact component={DemandBridgesView} />
        <PermissionRoute path="/supply/technology" exact component={TechnologyView} />
        <PermissionRoute path="/supply/fg-sfg" exact component={FgSfgSupplyView} />
        <PermissionRoute path="/supply/detail" exact component={SupplyDetailView} />
        <PermissionRoute path="/raw-and-packs/" exact component={RawAndPacksView} />
        <PermissionRoute path="/raw-and-packs/commitment" exact component={RawAndPacksCommitmentView} />
        <PermissionRoute path="/raw-and-packs/bom" exact component={RawAndPacksBomView} />
        <PermissionRoute path="/performance-monitoring/demand" exact component={PerformanceMonitoringDemandView} />
        <PermissionRoute path="/performance-monitoring/supply" exact component={PerformanceMonitoringSupplyView} />
        <PermissionRoute path="/help" exact component={HelpView} />
        <Route path="*">
          <Typography variant="h4" component="h1">404 error</Typography>
        </Route>
      </Switch>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <ThemeProvider theme={theme}>
        <Router>
          <CssBaseline />
          <MessagesView />
          <ScrollToTop />
          <Grid container wrap="nowrap" className={classes.mainContainer}>
            <Grid item xs className={classes.navigation}>
              <Navigation />
            </Grid>
            <Grid item xs>
              <Grid
                container
                direction="column"
                wrap="nowrap"
                className={classes.contentContainer}
              >
                <Grid item>
                  <NavigationSubMenu />
                </Grid>
                <Grid item xs className={classes.content}>
                  {this.content}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Router>
      </ThemeProvider>
    );
  }
}


export default App;
